import type { Command, SafetyState, Telemetry } from '../protocol';
import { PROTOCOL_VERSION } from '../protocol';

export const APP_VERSION = '0.1.0-phase0';

export interface SessionHeader {
  type: 'header';
  app_version: string;
  protocol_version: string;
  physics: string;
  map: string;
  map_version: string;
  profile: string;
  profile_calibration: { version: string; date: string | null };
  link_preset: string;
  seed: number;
  started_at: string;
  start_state: { pos: [number, number, number]; heading_deg: number };
}

export type LogRecord =
  | SessionHeader
  | { type: 'cmd'; t: number; cmd: Command }
  | { type: 'snap'; t: number; tm: Telemetry }
  | { type: 'event'; t: number; kind: string; detail?: string }
  | { type: 'state'; t: number; from?: SafetyState; to?: SafetyState; detail?: string }
  | {
      type: 'collision';
      t: number;
      what: string;
      speed_ms: number;
    }
  | { type: 'link'; t: number; preset: string; detail?: string };

/**
 * Лог сессии пишется всегда (п. 13 ТЗ). Формат — JSONL: одна запись в строке,
 * поток дописывается, файл читаем без парсера. Заголовок фиксирует версии всего,
 * что влияет на прогон, и seed — иначе разбор с оператором ничего не доказывает.
 */
export class SessionLog {
  private records: LogRecord[] = [];
  /** Читаемые строки для панели диагностики в UI */
  private diag: Array<{ t: number; text: string; level: 'info' | 'warn' | 'error' }> = [];
  private diagListeners: Array<() => void> = [];

  constructor(header: Omit<SessionHeader, 'type'>) {
    this.records.push({ type: 'header', ...header });
  }

  add(rec: LogRecord): void {
    this.records.push(rec);
  }

  /** Событие для человека: попадает и в лог, и в панель диагностики */
  note(t: number, text: string, level: 'info' | 'warn' | 'error' = 'info'): void {
    this.diag.push({ t, text, level });
    if (this.diag.length > 300) this.diag.shift();
    for (const l of this.diagListeners) l();
  }

  onDiag(listener: () => void): void {
    this.diagListeners.push(listener);
  }

  get diagnostics(): ReadonlyArray<{ t: number; text: string; level: 'info' | 'warn' | 'error' }> {
    return this.diag;
  }

  get size(): number {
    return this.records.length;
  }

  toJSONL(): string {
    return this.records.map((r) => JSON.stringify(r)).join('\n');
  }

  download(): void {
    const blob = new Blob([this.toJSONL()], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `robosim-session-${new Date().toISOString().replace(/[:.]/g, '-')}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async copyDiagnostics(): Promise<void> {
    const text = this.diag.map((d) => `${fmt(d.t)} ${d.text}`).join('\n');
    await navigator.clipboard.writeText(text);
  }
}

export function fmt(ms: number): string {
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}.${String(Math.floor(ms % 1000)).padStart(3, '0')}`;
}

export const PROTOCOL = PROTOCOL_VERSION;
